/**
 * Data Source Selection Store
 * Manages selected company and data source for multi-tenant analytics
 */

import { create } from 'zustand';
import { persist } from 'zustand/middleware';

interface DataSource {
  id: string;
  name: string;
  type: string;
  sourceType?: 'database' | 'file_upload' | 'api';
  config: {
    database: string;
    collection?: string;
  };
  availableModules?: string[];
  purpose?: string;
  isDefault: boolean;
  isActive: boolean;
}

interface Company {
  _id: string;
  name: string;
  slug: string;
  domain?: string;
  dataSources?: DataSource[];
}

interface DataSourceState {
  // Selected company (for super admin only)
  selectedCompany: Company | null;
  
  // Selected data sources (MULTI-SELECT)
  selectedDataSources: DataSource[];
  
  // Actions
  setSelectedCompany: (company: Company | null) => void;
  setSelectedDataSources: (dataSources: DataSource[]) => void;
  addDataSource: (dataSource: DataSource) => void;
  removeDataSource: (dataSourceId: string) => void;
  toggleDataSource: (dataSource: DataSource) => void;
  clearSelection: () => void;
  
  // Helper to get current selection for API calls
  getSelection: () => {
    companyId: string | null;
    dataSourceIds: string[];
    // Legacy support for single source (returns first selected)
    dataSourceId: string | null;
  };
}

export const useDataSourceStore = create<DataSourceState>()(
  persist(
    (set, get) => ({
      selectedCompany: null,
      selectedDataSources: [],

      setSelectedCompany: (company) => {
        set({ 
          selectedCompany: company,
          // Clear data sources when company changes
          selectedDataSources: [],
        });
      },

      setSelectedDataSources: (dataSources) => {
        set({ selectedDataSources: dataSources });
      },

      addDataSource: (dataSource) => {
        const state = get();
        if (!state.selectedDataSources.find(ds => ds.id === dataSource.id)) {
          set({ selectedDataSources: [...state.selectedDataSources, dataSource] });
        }
      },

      removeDataSource: (dataSourceId) => {
        const state = get();
        set({ 
          selectedDataSources: state.selectedDataSources.filter(ds => ds.id !== dataSourceId) 
        });
      },

      toggleDataSource: (dataSource) => {
        const state = get();
        const exists = state.selectedDataSources.find(ds => ds.id === dataSource.id);
        if (exists) {
          set({ 
            selectedDataSources: state.selectedDataSources.filter(ds => ds.id !== dataSource.id) 
          });
        } else {
          set({ selectedDataSources: [...state.selectedDataSources, dataSource] });
        }
      },

      clearSelection: () => {
        set({ 
          selectedCompany: null, 
          selectedDataSources: [] 
        });
      },

      getSelection: () => {
        const state = get();
        
        // CRITICAL FIX: For normal admins, get companyId from user object
        // Access authStore to determine if user is super admin
        try {
          const authStoreState = localStorage.getItem('auth-storage');
          if (authStoreState) {
            const { state: authState } = JSON.parse(authStoreState);
            const user = authState?.user;
            
            // Check if user is super admin - handle both string roles and role objects
            let isSuperAdmin = false;
            if (user?.roles && Array.isArray(user.roles)) {
              isSuperAdmin = user.roles.some((role: any) => {
                if (typeof role === 'string') {
                  return role === 'super-admin' || role === 'super_admin';
                } else if (role && typeof role === 'object') {
                  return role.slug === 'super-admin' || role.slug === 'super_admin';
                }
                return false;
              });
            }
            
            console.log('🔐 getSelection - isSuperAdmin:', isSuperAdmin, 'user:', user?.email, 'selected sources:', state.selectedDataSources.length);
            
            const dataSourceIds = state.selectedDataSources.map(ds => ds.id);
            
            return {
              // Super admin: use selected company, Normal admin: use their company from user
              companyId: isSuperAdmin 
                ? (state.selectedCompany?._id || null)
                : (user?.companyId || null),
              dataSourceIds: dataSourceIds,
              // Legacy support for single source (returns first selected)
              dataSourceId: dataSourceIds[0] || null,
            };
          }
        } catch (e) {
          console.error('❌ Failed to parse auth state:', e);
        }
        
        // Fallback to selected company
        const dataSourceIds = state.selectedDataSources.map(ds => ds.id);
        return {
          companyId: state.selectedCompany?._id || null,
          dataSourceIds: dataSourceIds,
          dataSourceId: dataSourceIds[0] || null,
        };
      },
    }),
    {
      name: 'data-source-selection', // localStorage key
    }
  )
);

